export const MIN_VISIBLE_STRAIGHT_JOIN_MM = 1000;
function finitePositive(value) {
    return Number.isFinite(value) && value > 0;
}
/**
 * Turns measured trim runs into an auditable piece-by-piece purchase plan.
 *
 * A long visible straight is balanced across its joins so it never ends with a
 * token offcut. Natural breaks (doorways and mitred corners) may use a short
 * piece. Compatible offcuts are then reused with deterministic first-fit
 * decreasing packing.
 */
export function planTrimPurchase(runs) {
    const warnings = [];
    const cuts = [];
    let measuredRunMM = 0;
    for (const run of runs) {
        if (!run.id || !run.label.trim() || !run.sku.trim() || !finitePositive(run.lengthMM) || !finitePositive(run.stockLengthMM)) {
            warnings.push(`${run.label || run.id || "Trim run"}: length, stock length and SKU are required.`);
            continue;
        }
        measuredRunMM += run.lengthMM;
        const segmentCount = Math.ceil(run.lengthMM / run.stockLengthMM);
        if (segmentCount === 1) {
            cuts.push({ runId: run.id, label: run.label, sku: run.sku, stockLengthMM: run.stockLengthMM, lengthMM: run.lengthMM, segment: 1, segmentCount: 1 });
            continue;
        }
        const balancedLength = run.lengthMM / segmentCount;
        const canBalance = run.breakKind === "natural" || balancedLength >= MIN_VISIBLE_STRAIGHT_JOIN_MM;
        if (!canBalance) {
            warnings.push(`${run.label}: ${Math.round(run.lengthMM)} mm cannot be joined from ${Math.round(run.stockLengthMM)} mm stock without a straight piece under ${MIN_VISIBLE_STRAIGHT_JOIN_MM} mm.`);
        }
        let remaining = run.lengthMM;
        for (let index = 0; index < segmentCount; index += 1) {
            const segmentsLeft = segmentCount - index;
            const lengthMM = canBalance ? remaining / segmentsLeft : Math.min(run.stockLengthMM, remaining);
            cuts.push({
                runId: run.id,
                label: run.label,
                sku: run.sku,
                stockLengthMM: run.stockLengthMM,
                lengthMM,
                segment: index + 1,
                segmentCount,
            });
            remaining -= lengthMM;
        }
    }
    const byStock = new Map();
    for (const cut of cuts) {
        const key = `${cut.sku}\u0000${cut.stockLengthMM}`;
        byStock.set(key, [...(byStock.get(key) ?? []), cut]);
    }
    const groups = [...byStock.values()].map((compatibleCuts) => {
        const { sku, stockLengthMM } = compatibleCuts[0];
        const bins = [];
        const ordered = [...compatibleCuts].sort((a, b) => b.lengthMM - a.lengthMM || a.runId.localeCompare(b.runId) || a.segment - b.segment);
        for (const cut of ordered) {
            let bin = bins.find((candidate) => candidate.remainderMM + 0.0001 >= cut.lengthMM);
            if (!bin) {
                bin = { cuts: [], remainderMM: stockLengthMM };
                bins.push(bin);
            }
            bin.cuts.push({ runId: cut.runId, label: cut.label, lengthMM: cut.lengthMM, segment: cut.segment, segmentCount: cut.segmentCount });
            bin.remainderMM -= cut.lengthMM;
        }
        const pieces = bins.map((bin, index) => ({ piece: index + 1, sku, stockLengthMM, cuts: bin.cuts, remainderMM: bin.remainderMM }));
        return { sku, stockLengthMM, piecesToBuy: pieces.length, pieces };
    }).sort((a, b) => a.sku.localeCompare(b.sku) || a.stockLengthMM - b.stockLengthMM);
    const piecesToBuy = groups.reduce((sum, group) => sum + group.piecesToBuy, 0);
    const purchasedMM = groups.reduce((sum, group) => sum + group.stockLengthMM * group.piecesToBuy, 0);
    return {
        groups,
        piecesToBuy,
        measuredRunMM,
        purchasedMM,
        remainderMM: purchasedMM - measuredRunMM,
        warnings,
        readyToOrder: runs.length > 0 && warnings.length === 0,
    };
}
//# sourceMappingURL=trim-purchase.js.map